import React, { ChangeEvent, useCallback } from "react";

import dayjs from "dayjs";
import customParse from "dayjs/plugin/customParseFormat";

import {
    Control,
    FieldPath,
    FieldValues,
    PathValue,
    useController,
} from "react-hook-form";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import colors, { blue, darkGrey, white } from "../../../../utils/colors";
import { IconPredefined } from "../../icons/Icon";
import { PredefinedIconType } from "../../icons/Icon/Predefined";
import { withField as withFieldWrapper } from "../Field";
import FormError from "../FormError";
import { ErrorIconWrapper, InputButton } from "../NewInput";
import ErrorMessage from "./ErrorMessage";
import { StyledInput } from "../NewInput";
import { Placeholder } from "../NewInput";
import { StyledTextarea } from "../NewInput";
import { Container } from "../NewInput";
dayjs.extend(customParse);

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const textareaPlaceholderStyles = css`
    align-items: flex-start;
    padding-top: 7px;
`;

const IconContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    padding-right: 10px;
`;

export type iInputProps<
    fvs extends FieldValues = FieldValues,
    name extends FieldPath<fvs> = FieldPath<fvs>,
    ctx = any
> = (undefined extends PathValue<fvs, name>
    ? {
          ifNullish: NonNullable<PathValue<fvs, name>>;
          asUndefined?: NonNullable<PathValue<fvs, name>>;
      }
    : Record<never, string>) &
    (null extends PathValue<fvs, name>
        ? {
              ifNullish: NonNullable<PathValue<fvs, name>>;
              asNull?: NonNullable<PathValue<fvs, name>>;
          }
        : Record<never, string>) &
    (PathValue<fvs, name> extends string | number | undefined | null
        ? {
              fullWidth?: boolean;
              name: name;
              type?: string;
              id?: string | null;
              isDisabled?: boolean;
              withField?: boolean;
              maxWidth?: any;
              placeholder: string;
              errors?: any;
              icon?: PredefinedIconType;
              resetField?: any;
              handleClick?: any;
              onKeyDown?: React.KeyboardEventHandler<
                  HTMLInputElement | HTMLTextAreaElement
              >;
              defaultValue?: PathValue<fvs, name>;
              rules: any;
              control: Control<fvs, ctx>;
          }
        : never);

const Input = <
    fvs extends FieldValues = FieldValues,
    name extends FieldPath<fvs> = FieldPath<fvs>,
    ctx = any
>({
    fullWidth = false,
    type = "text",
    isDisabled = false,
    withField = false,
    maxWidth = undefined,
    placeholder = "",
    errors,
    icon,
    resetField,
    handleClick,
    onKeyDown,
    ...props
}: iInputProps<fvs, name, ctx>) => {
    const { field, fieldState } = useController<fvs, name>(props);

    const { ifNullish, asUndefined, asNull } = props as any as {
        ifNullish?: NonNullable<PathValue<fvs, name>>;
        asUndefined?: NonNullable<PathValue<fvs, name>>;
        asNull?: NonNullable<PathValue<fvs, name>>;
    };

    const value = (field.value ?? ifNullish) as NonNullable<
        PathValue<fvs, name>
    >;

    const onChange = useCallback(
        ({
            target: { value },
        }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (value === asUndefined) {
                field.onChange(undefined);
            } else if (value === asNull) {
                field.onChange(null);
            } else {
                field.onChange(value);
            }
        },
        [asUndefined, asNull, field]
    );

    const component = (
        <Wrapper
            css={css`
                ${fullWidth ? "width: 100%;" : "width: fit-content;"}
            `}
        >
            <Container
                css={css`
                    ${fullWidth ? "width: 100%;" : "width: fit-content;"}
                `}
            >
                {type === "textarea" ? (
                    <StyledTextarea
                        Component="textarea"
                        placeholder=" "
                        disabled={isDisabled}
                        id={props.name as any}
                        hasError={fieldState.error}
                        {...field}
                        value={value as any}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                    />
                ) : (
                    <StyledInput
                        placeholder=" "
                        autoComplete={
                            [
                                "zipcode",
                                "consumption",
                                "street",
                                "houseNumber",
                                "streetSelectList",
                                "placeSelectList",
                                "houseNumber",
                                "ownerName",
                                "ownerStreet",
                                "ownerStreetNumber",
                                "ownerZipcode",
                                "ownerCity",
                            ].includes(props.name)
                                ? "off"
                                : "on"
                        }
                        disabled={isDisabled}
                        id={props.name as any}
                        hasError={fieldState.error}
                        {...field}
                        {...{ type, value, onChange, onKeyDown, ...props }}
                    />
                )}

                <Placeholder
                    htmlFor={props.name}
                    css={
                        type === "textarea"
                            ? textareaPlaceholderStyles
                            : undefined
                    }
                >
                    {placeholder}
                </Placeholder>

                {type !== "textarea" && icon && (
                    <IconContainer>
                        <IconPredefined type={icon} size="20px" color={blue} />
                    </IconContainer>
                )}

                {type !== "textarea" && resetField && (
                    <IconContainer
                        css={css`
                            ${handleClick && "padding-right: 60px;"}
                            ${handleClick &&
                            fieldState.error &&
                            "padding-right: 85px;"}
                        `}
                    >
                        <IconPredefined
                            css={css`
                                cursor: pointer;
                            `}
                            type={PredefinedIconType.close}
                            size="20px"
                            color={darkGrey}
                            onClick={() =>
                                resetField(props.name, { defaultValue: "" })
                            }
                        />
                    </IconContainer>
                )}

                {type !== "textarea" && handleClick && (
                    <InputButton onClick={() => handleClick()}>
                        <IconPredefined
                            type={PredefinedIconType.search}
                            size="20px"
                            color={white}
                        />
                    </InputButton>
                )}

                {fieldState.error && (
                    <ErrorIconWrapper
                        css={css`
                            ${handleClick && "padding-right: 45px;"}
                        `}
                    >
                        <IconPredefined
                            type={PredefinedIconType.achtung}
                            color={colors.red}
                        />
                    </ErrorIconWrapper>
                )}
            </Container>
            {fieldState.error && (
                <ErrorMessage
                    error={fieldState.error}
                    name={placeholder}
                    rules={props.rules}
                />
            )}

            {errors && Array.isArray(errors) && (
                <FormError gtag={`error:${props.name}`} message={errors[0]} />
            )}
        </Wrapper>
    );

    return withField ? withFieldWrapper(component) : component;
};

export default Input;
